<template>
  <!-- Hero -->
  <section class="container-xxl section hero overflow-hidden">
    <div class="row align-items-center g-5">
      <div class="col-lg-6">
        <div class="eyebrow mb-2">
          <span class="badge badge-tinted-primary border">Non‑custodial · Testnet ready</span>
        </div>
        <h1 class="display-tight fw-bold mb-3">
          Launch a polished <span class="gradient-text">DeFi swap</span> in minutes
        </h1>
        <p class="lead text-muted-token">
          A brandable, production‑minded starter for token swaps. Connect your wallet, fetch quotes, and execute — all without custody.
        </p>
        <div class="d-flex flex-wrap gap-3 mt-4">
          <router-link to="/swap" class="btn btn-primary btn-lg">
            <i class="bi bi-lightning-charge me-2"></i>Launch demo
          </router-link>
          <router-link to="/docs" class="btn btn-outline-secondary btn-lg">
            <i class="bi bi-journal-text me-2"></i>Read docs
          </router-link>
        </div>
        <div class="d-flex flex-wrap gap-3 mt-4 small text-muted-token">
          <span class="d-inline-flex align-items-center gap-1"><i class="bi bi-shield-check"></i>Non‑custodial</span>
          <span class="d-inline-flex align-items-center gap-1"><i class="bi bi-speedometer2"></i>Fast quotes</span>
          <span class="d-inline-flex align-items-center gap-1"><i class="bi bi-palette"></i>Brandable UI</span>
        </div>
      </div>
      <div class="col-lg-6">
        <!-- Geometric composition for a Bauhaus feel -->
        <div class="shape-stack mx-auto">
          <!-- <div class="shape circle"></div> -->
          <!-- <div class="shape square"></div> -->
          <!-- Lottie JSON: Spinning coin -->
          <lottie-player
            ref="coinLottie"
            class="shape lottie coin"
            src="/assets/3D%20Floating%20Spinning%20Coin.json"
            background="transparent"
            speed="0.05"
            loop
            autoplay
            aria-hidden="true"
          ></lottie-player>
          <!-- Decorative Lottie JSON animation among shapes -->
          <lottie-player
            ref="heroLottie"
            class="shape lottie"
            src="/assets/3D%20Shape%20Animation.json"
            background="transparent"
            speed="0.50"
            loop
            autoplay
            aria-hidden="true"
          ></lottie-player>
        </div>
      </div>
    </div>
  </section>

  <!-- Value props -->
  <section class="container-xxl section">
    <div class="row g-5">
      <div class="col-md-4">
        <div class="card h-100 roomy-card">
          <div class="d-flex align-items-center gap-2 mb-2"><i class="bi bi-box-seam fs-5 text-primary"></i><h5 class="mb-0">Plug‑and‑play</h5></div>
          <p class="text-muted-token mb-0">Vue 3 + TypeScript, Bootstrap styling, WalletConnect support — ready to fork and brand.</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100 roomy-card">
          <div class="d-flex align-items-center gap-2 mb-2"><i class="bi bi-shield-lock fs-5 text-primary"></i><h5 class="mb-0">Non‑custodial</h5></div>
          <p class="text-muted-token mb-0">Users remain in control of their wallets. Clear states and safe defaults.</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100 roomy-card">
          <div class="d-flex align-items-center gap-2 mb-2"><i class="bi bi-speedometer fs-5 text-primary"></i><h5 class="mb-0">Fast quotes</h5></div>
          <p class="text-muted-token mb-0">Fetch estimates from Uniswap‑style pools with straightforward, typed composables.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="container-xxl section">
    <div class="row g-5 align-items-center">
      <div class="col-lg-5">
        <h2 class="fw-bold mb-2">How it works</h2>
        <p class="text-muted-token">A concise flow built for clarity and speed. You can ship a clean demo and iterate quickly.</p>
      </div>
      <div class="col-lg-7">
        <div class="row g-3">
          <div class="col-sm-4">
            <div class="p-4 rounded surface-2 h-100">
              <div class="stat stat-lg">1</div>
              <div class="fw-semibold">Connect</div>
              <div class="small text-muted-token">WalletConnect/AppKit or injected wallet.</div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="p-4 rounded surface-2 h-100">
              <div class="stat stat-lg">2</div>
              <div class="fw-semibold">Quote</div>
              <div class="small text-muted-token">Choose tokens and fetch an estimate.</div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="p-4 rounded surface-2 h-100">
              <div class="stat stat-lg">3</div>
              <div class="fw-semibold">Swap</div>
              <div class="small text-muted-token">Approve and submit on testnet.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Stay in the loop / Lead form -->
  <section class="container-xxl section">
    <div class="row g-4 justify-content-center">
      <div class="col-12 col-lg-10 col-xl-8">
        <div class="card roomy-card">
          <h3 class="fw-bold mb-1">Stay in the loop</h3>
          <p class="text-muted-token mb-4">Leave your email — we’ll share updates and roadmap invites.</p>
          <form class="row gy-3" @submit.prevent="submit">
            <!-- Primary inline email + submit on md+ -->
            <div class="col-12">
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-envelope"></i></span>
                <input
                  v-model.trim="email"
                  :class="['form-control', invalidEmail ? 'is-invalid' : '']"
                  type="email"
                  placeholder="you@example.com"
                  aria-label="Email address"
                />
                <button class="btn btn-primary" type="submit" :disabled="submitting || invalidEmail">
                  {{ submitting ? 'Sending…' : 'Notify me' }}
                </button>
              </div>
              <div v-if="invalidEmail" class="invalid-feedback d-block">Please provide a valid email.</div>
            </div>

            <!-- Optional details -->
            <div class="col-12 col-md-4">
              <input v-model.trim="name" class="form-control" placeholder="Name (optional)" />
            </div>
            <div class="col-12 col-md-8">
              <input v-model.trim="message" class="form-control" placeholder="Message (optional)" />
            </div>
          </form>
          <div v-if="sent" class="alert alert-success mt-3">Thanks! We’ll be in touch.</div>
          <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { submitLead, track } from '../composables/telemetry';
import { useToastStore } from '../stores/toast';

const name = ref('');
const email = ref('');
const message = ref('');
const submitting = ref(false);
const sent = ref(false);
const error = ref<string | null>(null);
const toast = useToastStore();
const invalidEmail = computed(() => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value || ''));

// Respect reduced-motion preference for the decorative Lottie
const heroLottie = ref<any | null>(null);
const lottieError = ref(false);
const coinLottie = ref<any | null>(null);
const coinError = ref(false);
let motionMql: MediaQueryList | null = null;
let motionHandler: (() => void) | null = null;

onMounted(() => {
  motionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
  const apply = () => {
    const player = heroLottie.value as any;
    if (!player || typeof player.pause !== 'function') return;
    if (motionMql?.matches) player.pause();
    else if (typeof player.play === 'function') player.play();
  };
  apply();
  if (motionMql) {
    if ('addEventListener' in motionMql) motionMql.addEventListener('change', apply);
    // Safari fallback
    // @ts-ignore
    else motionMql.addListener(apply);
    motionHandler = apply;
  }

  // Hide the player if it errors (e.g., missing asset)
  const playerEl = heroLottie.value as any;
  if (playerEl && typeof playerEl.addEventListener === 'function') {
    playerEl.addEventListener('error', () => { lottieError.value = true; });
  }

  const coinEl = coinLottie.value as any;
  if (coinEl && typeof coinEl.addEventListener === 'function') {
    coinEl.addEventListener('error', () => { coinError.value = true; });
  }
});

onBeforeUnmount(() => {
  if (motionMql && motionHandler) {
    if ('removeEventListener' in motionMql) motionMql.removeEventListener('change', motionHandler);
    // Safari fallback
    // @ts-ignore
    else motionMql.removeListener(motionHandler);
  }
});

async function submit() {
  submitting.value = true;
  error.value = null;
  try {
    await submitLead(name.value, email.value, message.value);
    sent.value = true;
    await track('lead_submitted', { email: email.value ? 'provided' : 'missing' });
    toast.push('success', 'Thanks! We\'ll be in touch.');
    name.value = email.value = message.value = '';
  } catch (e: any) {
    error.value = 'Something went wrong — try again';
    toast.push('error', error.value);
  } finally {
    submitting.value = false;
  }
}
</script>
